import { memo, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button, Dialog, DialogContent, Link, Stack, TextField, Typography, useTheme } from '@mui/material'
import { MuiOtpInput } from 'mui-one-time-password-input'
import { primary } from '../../assets/colors'
import { selectIsEmbedded } from '../../selectors/device'
import { selectUserDetails } from '../../selectors/user'
import { setUserDetails } from '../../reducer/user.reducer'
import {
	clearPinSetupToken,
	getPinSetupGrant,
	isPinEnabledFlag,
	setMobileVerifiedFlag,
	setPinEnabledFlag,
} from '../../util/pinBrowserFlags'
import { setLoginSessionWindow } from '../../util/loginSessionWindow'
import {
	EMAIL_OTP_LENGTH,
	getApiErrorMessage,
	getPinPairError,
	PIN_DIGITS,
	requestPinEmailOtp,
	sanitizeDigitInput,
	savePinInitial,
	savePinWithEmailOtp,
} from '../../util/securityCredentials'
import { parseErrorFromArrayBuffer } from '../common/errorParser'
import { ErrorCodes } from '../../proto/generated/common'

type Step = 'pin' | 'otp'

/**
 * Blocking in-app PIN setup after mobile is verified.
 * Reuses SMS OTP + requestId_2 from mobile verify when still in memory; otherwise PIN OTP.
 */
const PinSetupAlert = () => {
	const theme = useTheme()
	const dispatch = useDispatch()
	const isEmbedded = useSelector(selectIsEmbedded)
	const userDetails = useSelector(selectUserDetails)
	const user = userDetails?.currentUser
	const open =
		!isEmbedded &&
		!!user &&
		!!user.mobileVerified &&
		!user.isPinEnabled &&
		!isPinEnabledFlag()

	const [step, setStep] = useState<Step>('pin')
	const [pin, setPin] = useState('')
	const [confirmPin, setConfirmPin] = useState('')
	const [otp, setOtp] = useState('')
	const [requestId, setRequestId] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [busy, setBusy] = useState(false)
	const [otpLocked, setOtpLocked] = useState(false)

	useEffect(() => {
		if (!open) {
			setStep('pin')
			setPin('')
			setConfirmPin('')
			setOtp('')
			setRequestId('')
			setError(null)
			setOtpLocked(false)
		}
	}, [open])

	const finishSuccess = useCallback(() => {
		setPinEnabledFlag(true)
		setMobileVerifiedFlag(true)
		clearPinSetupToken()
		setLoginSessionWindow()
		if (user) {
			dispatch(
				setUserDetails({
					...userDetails,
					authenticated: true,
					currentUser: { ...user, isPinEnabled: true, mobileVerified: true },
				})
			)
		}
	}, [dispatch, user, userDetails])

	const submitWithGrantOrOtp = useCallback(async () => {
		const pairError = getPinPairError(pin, confirmPin)
		if (pairError) {
			setError(pairError)
			return
		}
		setBusy(true)
		setError(null)
		try {
			const grant = getPinSetupGrant()
			if (grant) {
				try {
					await savePinInitial(pin, grant)
					finishSuccess()
					return
				} catch (grantErr) {
					clearPinSetupToken()
					const msg = getApiErrorMessage(grantErr, '')
					if (!/expired|verification|otp|session|required/i.test(msg)) {
						throw grantErr
					}
				}
			}
			const id = await requestPinEmailOtp()
			setRequestId(id)
			setStep('otp')
			setOtp('')
			setOtpLocked(false)
		} catch (e) {
			const parsed = parseErrorFromArrayBuffer(e)
			if (parsed.errorCode === ErrorCodes.RATE_LIMIT_EXCEEDED) {
				setStep('otp')
				setOtpLocked(true)
				setOtp('')
				setError(parsed.message || 'Too many OTP requests. Please try again later.')
				return
			}
			setError(getApiErrorMessage(e, 'Could not save PIN'))
		} finally {
			setBusy(false)
		}
	}, [confirmPin, finishSuccess, pin])

	const submitWithOtp = useCallback(async () => {
		if (otpLocked) return
		if (otp.length !== EMAIL_OTP_LENGTH) {
			setError(`Enter the ${EMAIL_OTP_LENGTH}-digit OTP`)
			return
		}
		setBusy(true)
		setError(null)
		try {
			await savePinWithEmailOtp({
				pin,
				verificationRequestId: requestId,
				verificationOtp: otp,
			})
			finishSuccess()
		} catch (e) {
			const parsed = parseErrorFromArrayBuffer(e)
			if (parsed.errorCode === ErrorCodes.RATE_LIMIT_EXCEEDED) {
				setOtpLocked(true)
				setOtp('')
				setError('Too many incorrect attempts. Please resend OTP.')
				return
			}
			setError(parsed.message || getApiErrorMessage(e, 'Invalid OTP'))
			setOtp('')
		} finally {
			setBusy(false)
		}
	}, [finishSuccess, otp, otpLocked, pin, requestId])

	const handleResendOtp = useCallback(async () => {
		setBusy(true)
		setError(null)
		try {
			const id = await requestPinEmailOtp()
			setRequestId(id)
			setOtp('')
			setOtpLocked(false)
		} catch (e) {
			const parsed = parseErrorFromArrayBuffer(e)
			if (parsed.errorCode === ErrorCodes.RATE_LIMIT_EXCEEDED) {
				setOtpLocked(true)
				setOtp('')
				setError(parsed.message || 'Too many OTP requests. Please try again later.')
				return
			}
			setError(getApiErrorMessage(e, 'Could not resend OTP'))
		} finally {
			setBusy(false)
		}
	}, [])

	if (!open) return null

	return (
		<Dialog
			open
			disableEscapeKeyDown
			fullWidth
			maxWidth="xs"
			PaperProps={{
				sx: {
					borderRadius: 2,
					bgcolor: theme.palette.background.paper,
				},
			}}
			slotProps={{
				backdrop: {
					sx: { backdropFilter: 'blur(4px)' },
				},
			}}
		>
			<DialogContent>
				<Stack spacing={2} sx={{ py: 1 }}>
					<Typography variant="h6" fontWeight={600}>
						{step === 'pin' ? 'Set up your PIN' : 'Verify OTP'}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{step === 'pin'
							? 'Create a 4-digit PIN to secure your account. You will need it every time you log in.'
							: 'Enter the OTP sent to your email (and mobile if available).'}
					</Typography>
					{step === 'pin' ? (
						<>
							<TextField
								label="PIN"
								type="password"
								value={pin}
								onChange={(e) => {
									setPin(sanitizeDigitInput(e.target.value, PIN_DIGITS))
									if (error) setError(null)
								}}
								inputProps={{ inputMode: 'numeric', maxLength: PIN_DIGITS }}
								fullWidth
							/>
							<TextField
								label="Confirm PIN"
								type="password"
								value={confirmPin}
								onChange={(e) => {
									setConfirmPin(sanitizeDigitInput(e.target.value, PIN_DIGITS))
									if (error) setError(null)
								}}
								inputProps={{ inputMode: 'numeric', maxLength: PIN_DIGITS }}
								fullWidth
								error={Boolean(error)}
								helperText={error || ' '}
							/>
							<Button
								variant="contained"
								disabled={busy || pin.length !== PIN_DIGITS || confirmPin.length !== PIN_DIGITS}
								onClick={() => void submitWithGrantOrOtp()}
								sx={{ background: primary }}
							>
								{busy ? 'Saving…' : 'Continue'}
							</Button>
						</>
					) : (
						<>
							<MuiOtpInput
								value={otp}
								length={EMAIL_OTP_LENGTH}
								onChange={(v) => {
									if (otpLocked) return
									setOtp(sanitizeDigitInput(v, EMAIL_OTP_LENGTH))
									if (error) setError(null)
								}}
								TextFieldsProps={{
									disabled: busy || otpLocked,
								}}
							/>
							<Typography
								color="error"
								variant="body2"
								sx={{ minHeight: 20, visibility: error ? 'visible' : 'hidden' }}
							>
								{error || '\u00a0'}
							</Typography>
							{!otpLocked && (
								<Button
									variant="contained"
									disabled={busy || otp.length !== EMAIL_OTP_LENGTH}
									onClick={() => void submitWithOtp()}
									sx={{ background: primary }}
								>
									{busy ? 'Verifying…' : 'Set PIN'}
								</Button>
							)}
							{otpLocked && (
								<Link
									component="button"
									type="button"
									onClick={() => void handleResendOtp()}
									disabled={busy}
									sx={{ cursor: busy ? 'default' : 'pointer' }}
								>
									{busy ? 'Sending OTP…' : 'Resend OTP'}
								</Link>
							)}
						</>
					)}
				</Stack>
			</DialogContent>
		</Dialog>
	)
}

export default memo(PinSetupAlert)
